import React, { useMemo } from "react";
import { Box, Chip, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CategoryDetails from "./CategoryDetails";
import FieldDetails from "./FieldDetails";
import FieldList from "./FieldList";
import { SKIPPED_FIELDS_KEY } from "../../../util/constants";
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from "react-redux";
import CustomButton from "../../../components/@extended/CustomButton";
import { useResponsive } from "../../../hooks/ResponsiveProvider";
import { getStatusColor } from "../../util";
import QuizIcon from '@mui/icons-material/Quiz';
import { checkFieldVisibility } from "../../dynamicFormEngine/utils/dependsOnValidationService";
import HiddenFieldQueryDrawer from "../commentHistory/HiddenFieldQueryDrawer";

const FormDetails = (props) => {
    const {isSmallScreen} = useResponsive();
    const { details, answers, sectionKey, subSectionKey, stepKey, openStepForm, editAllowed, formStatus, userSiteTrialRole, readOnlyActions, verifyAction, formConfig, queriesByFieldKey, hasOpenQueries } = props;

    // Calculate query counts by status from all fields
    const queryStatusCounts = useMemo(() => {
        if (!queriesByFieldKey) return { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
        
        const counts = { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
        
        Object.values(queriesByFieldKey).forEach((queries) => {
            queries.forEach((query) => {
                if (counts.hasOwnProperty(query.status)) {
                    counts[query.status]++;
                }
                counts.total++;
            });
        });
        
        return counts;
    }, [queriesByFieldKey]);

    // Get status label for display
    const getStatusLabel = (status) => {
        switch (status) {
            case 'OPEN': return 'Open';
            case 'RESOLVED': return 'Resolved';
            case 'VERIFIED': return 'Verified';
            case 'REOPENED': return 'Reopened';
            default: return status;
        }
    };

    return (
      <>
        {details && (
          <>
            <Box
              sx={{
                backgroundColor: '#FFF5E6',
                paddingX: isSmallScreen ? 1 : 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ paddingY: isSmallScreen ? 1 : 2 }}>
                {details.label}
              </Typography>
                {/* Query Status Summary */}
                {queryStatusCounts.total > 0 && (
                  <Tooltip 
                    title={`Total ${queryStatusCounts.total} ${queryStatusCounts.total === 1 ? 'query' : 'queries'} on this form`}
                    arrow
                    placement="top"
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      <QuizIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      {['OPEN', 'REOPENED', 'RESOLVED', 'VERIFIED'].map((status) => (
                        queryStatusCounts[status] > 0 && (
                          <Chip
                            key={status}
                            label={`${getStatusLabel(status)}: ${queryStatusCounts[status]}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              backgroundColor: getStatusColor(status).bg,
                              color: getStatusColor(status).text,
                              '& .MuiChip-label': {
                                paddingX: 1,
                              },
                            }}
                          />
                        )
                      ))}
                    </Stack>
                  </Tooltip>
                )}
              </Box>
              {editAllowed && (
                <IconButton onClick={() => openStepForm(details.key)}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            <Box sx={{ padding: isSmallScreen ? 1 : 2 }}>
              {details &&
                details.categories &&
                details.categories.map(
                  (category, index) =>
                    !SKIPPED_FIELDS_KEY.includes(category.key) &&
                    (category.showInReadOnly == undefined || category.showInReadOnly) &&
                    checkFieldVisibility(category, answers, true) && (
                      <CategoryDetails
                        key={category.key}
                        {...props}
                        details={category}
                        sectionKey={sectionKey}
                        subSectionKey={subSectionKey}
                        stepKey={stepKey}
                        formLabel={details.label}
                      />
                    )
                )}
              {details && details.fields && (
                <FieldList {...props} fieldArr={details.fields} subSectionKey={details.key} formLabel={details.label} />
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: isSmallScreen ? 1 : 2, pr: 0 }}>
              {readOnlyActions &&
                readOnlyActions.map(
                  (actions) =>
                    actions?.applicableStatus.includes(formStatus) &&
                    actions?.actionData?.role.includes(userSiteTrialRole) && (
                      <CustomButton
                        sx={{ mr: 2 }}
                        onClick={() => {
                          verifyAction({ stepKey, sectionKey, subSectionKey, answers });
                        }}
                      >
                        {actions?.label}
                      </CustomButton>
                    )
                )}
            </Box>
            <HiddenFieldQueryDrawer
              sectionDetails={details}
              answers={answers}
              userSiteTrialRole={userSiteTrialRole}
              formConfig={formConfig}
              sectionKey={sectionKey}
              subSectionKey={subSectionKey}
              stepKey={stepKey}
              formLabel={details?.label}
            />
          </>
        )}
      </>
    );
}

// 


export default FormDetails;